/**
 * Tool Registry System
 * Auto-discovers and registers all tools from gui/src/tools/
 */

import type {
  ToolManifest,
  ToolRenderer,
  ToolResult,
  ProgressUpdate
} from './types';

import { createFileLogger } from '../utils/logger';
import { createRiverOS } from './sdk';
import { useCommandRegistryStore } from '../stores/commandRegistryStore';
import { canonicalToolRendererRegistry } from '../components/Space/SpaceChat/renderers/canonical/CanonicalToolRendererRegistry';
import useUserStore from '../stores/userStore';

const log = createFileLogger('TOOL_REGISTRY', { enabled: false });

interface ToolRegistration {
  manifest: ToolManifest;
  handler: (params: any, riverOS: any) => Promise<ToolResult>;
  renderer?: ToolRenderer;
  views: Record<string, any>;
  upgradeModal?: React.ComponentType<{ isOpen: boolean; onClose: () => void }>;
}

class ToolRegistry {
  private tools = new Map<string, ToolRegistration>();
  private manifests = new Map<string, ToolManifest>();
  private initialized = false;
  private loading = false;

  async loadAllTools() {
    if (this.loading) {
      log.info('[ToolRegistry] Load already in progress, skipping');
      return;
    }

    this.loading = true;
    log.info('[ToolRegistry] Starting tool loading...');

    await this.loadFilesystemTools();

    log.info(`[ToolRegistry] ✅ Loaded ${this.tools.size} tools`);

    this.initialized = true;
    this.loading = false;
  }

  private async loadFilesystemTools() {
    log.info('[ToolRegistry] Loading filesystem tools...');

    try {
      const toolContexts = (require as any).context(
        './',
        true,
        /^\.\/[^/]+\/tool\.json$/
      );

      const toolKeys = toolContexts.keys();
      log.info(`[ToolRegistry] Found ${toolKeys.length} filesystem tool manifests`);

      const failed: Array<{ key: string; manifest: ToolManifest; toolDir: string }> = [];

      for (const key of toolKeys) {
        try {
          const manifest = toolContexts(key) as ToolManifest;
          const toolDir = key.split('/')[1];

          log.info(`[ToolRegistry] Loading tool: ${manifest.id} from ${toolDir}`);

          await this.loadTool(toolDir, manifest);
        } catch (error) {
          const manifest = toolContexts(key) as ToolManifest;
          const toolDir = key.split('/')[1];
          log.warn(`[ToolRegistry] Failed to load tool ${manifest.id}, will retry:`, error);
          failed.push({ key, manifest, toolDir });
        }
      }

      if (failed.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        for (const { manifest, toolDir } of failed) {
          try {
            log.info(`[ToolRegistry] Retrying tool: ${manifest.id}`);
            await this.loadTool(toolDir, manifest);
          } catch (error) {
            log.error(`[ToolRegistry] Failed to load tool ${manifest.id} after retry:`, error);
          }
        }
      }

      log.info(`[ToolRegistry] Successfully loaded ${this.tools.size} filesystem tools`);
      log.info(`[ToolRegistry] Registered tools:`, Array.from(this.manifests.keys()));
    } catch (error) {
      log.error('[ToolRegistry] Filesystem tool discovery failed:', error);
    }
  }

  private async loadTool(toolDir: string, manifest: ToolManifest) {
    try {
      // Load tool handler (required)
      const toolModule = await import(`./${toolDir}/index.ts`);
      
      if (!toolModule.default || !toolModule.default.handler) {
        throw new Error(`Tool ${manifest.id} must export default with handler`);
      }

      // Load renderer (optional)
      let renderer: ToolRenderer | undefined;
      try {
        const rendererModule = await import(`./${toolDir}/renderer.tsx`);
        renderer = rendererModule.default;
      } catch {
        log.info(`[ToolRegistry] No renderer for ${manifest.id}`);
      }

      // Load entity views (optional)
      const views: Record<string, any> = {};
      if (manifest.customEntities) {
        for (const entityType of manifest.customEntities) {
          try {
            const viewModule = await import(`./${toolDir}/views/${entityType}View.tsx`);
            views[entityType] = viewModule[`${entityType}View`] || viewModule.default;
            log.info(`[ToolRegistry] Loaded view for ${entityType}`);
          } catch (error) {
            log.warn(`[ToolRegistry] No view found for ${entityType}:`, error);
          }
        }
      }

      // Load upgrade modal (optional)
      let upgradeModal: any;
      if (manifest.upgradeModal) {
        try {
          const modalModule = await import(`./${toolDir}/upgrade-modal.tsx`);
          upgradeModal = modalModule.default;
          log.info(`[ToolRegistry] Loaded upgrade modal for ${manifest.id}`);
        } catch {
          log.warn(`[ToolRegistry] No upgrade modal for ${manifest.id}`);
        }
      }

      // Register the tool
      this.registerTool({
        manifest,
        handler: toolModule.default.handler,
        renderer,
        views,
        upgradeModal
      });
    } catch (error) {
      log.error(`[ToolRegistry] Failed to load tool ${manifest.id}:`, error);
      throw error;
    }
  }

  private registerTool(registration: ToolRegistration) {
    const { manifest, handler, renderer, views } = registration;

    log.info(`[ToolRegistry] Registering tool: ${manifest.id}`);

    // Store registration
    this.tools.set(manifest.id, registration);
    this.manifests.set(manifest.id, manifest);

    // Register command handler
    const wrappedHandler = async (params: any, context: any) => {
      log.info(`[ToolRegistry] Executing tool: ${manifest.id}`, params);

      // Get user context
      const userStore = useUserStore.getState();
      const userPlan = userStore.plan?.plan?.toLowerCase() as 'free' | 'plus' | 'ultra' | 'pro' || 'free';
      const userEmail = userStore.profile?.skeleton?.email || userStore.profile?.skeleton?.auth0?.email || '';

      // Create riverOS context
      const riverOS = createRiverOS({
        userId: context.userId,
        spaceId: context.spaceId,
        threadId: context.threadId,
        executionId: context.executionId,
        traceId: context.traceId,
        userPlan,
        userEmail,
        toolId: manifest.id,
        onProgress: context.onProgress
      });

      // Execute tool handler
      try {
        const result = await handler(params, riverOS);
        return result;
      } catch (error) {
        log.error(`[ToolRegistry] Tool execution failed: ${manifest.id}`, error);
        return riverOS.error(error instanceof Error ? error.message : 'Tool execution failed');
      }
    };

    // Register with command registry
    const commandRegistry = useCommandRegistryStore.getState();
    commandRegistry.registerCommand(manifest.id, wrappedHandler);

    // Create and register command definition
    const commandDefinition: any = {
      id: manifest.id,
      description: manifest.description,
      version: manifest.version || '1.0.0',
      
      schema: {
        parameters: manifest.parameters.map((p: any) => ({
          name: p.name,
          type: p.type,
          description: p.description,
          required: p.required,
          defaultValue: p.defaultValue
        })),
        contextRequirements: {}
      },
      
      handler: wrappedHandler,
      
      performance: manifest.performance,
      
      // Add formatViewData if tool result has structured data
      formatViewData: (result: any, toolInput: any) => {
        if (result?.data) {
          return {
            type: 'tool_result',
            summary: result.message || 'Tool complete',
            data: result.data,
            meta: {
              duration: result.metadata?.executionTime
            }
          };
        }
        return null;
      },
      
      // Convert to Claude tool schema
      toClaude: () => ({
        name: manifest.id,
        description: manifest.description,
        input_schema: {
          type: 'object',
          properties: manifest.parameters.reduce((acc: any, p: any) => {
            acc[p.name] = {
              type: p.type,
              description: p.description
            };
            return acc;
          }, {} as Record<string, any>),
          required: manifest.parameters.filter((p: any) => p.required).map((p: any) => p.name)
        }
      })
    };

    commandRegistry.registerCommandDefinition(commandDefinition);

    // Register renderer if exists (adapt to CanonicalToolRenderer interface)
    if (renderer) {
      const adaptedRenderer: any = {
        icon: renderer.icon,
        
        renderExecuting: (context: any) => {
          return renderer.renderExecuting({
            execution: {
              progress: typeof context.execution.progress === 'string' 
                ? { message: context.execution.progress }
                : context.execution.progress,
              result: context.execution.result,
              error: context.execution.error
            }
          });
        },
        
        renderCompleted: (context: any) => {
          return renderer.renderCompleted({
            execution: {
              progress: typeof context.execution.progress === 'string'
                ? { message: context.execution.progress }
                : context.execution.progress,
              result: context.execution.result,
              error: context.execution.error
            }
          });
        },
        
        renderFailed: (context: any) => {
          return renderer.renderFailed({
            execution: {
              progress: typeof context.execution.progress === 'string'
                ? { message: context.execution.progress }
                : context.execution.progress,
              result: context.execution.result,
              error: context.execution.error
            },
            error: context.execution.error || 'Unknown error'
          });
        }
      };
      
      canonicalToolRendererRegistry.register(manifest.id, adaptedRenderer);
      log.info(`[ToolRegistry] Registered renderer for ${manifest.id}`);
    }

    // Register entity views if exist
    for (const [entityType, viewComponent] of Object.entries(views)) {
      // TODO: Register with entity view registry
      log.info(`[ToolRegistry] Registered view for ${entityType}`);
    }

    log.info(`[ToolRegistry] Successfully registered tool: ${manifest.id}`);
  }

  getTool(toolId: string): ToolRegistration | null {
    return this.tools.get(toolId) || null;
  }

  getManifest(toolId: string): ToolManifest | null {
    return this.manifests.get(toolId) || null;
  }

  getAllTools(): ToolManifest[] {
    return Array.from(this.manifests.values());
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getToolsForPlacement(placement: keyof ToolManifest['placement']): ToolManifest[] {
    if (!this.initialized) {
      log.warn('[ToolRegistry] getToolsForPlacement called before initialization, returning empty');
      return [];
    }
    
    const tools = Array.from(this.manifests.values())
      .filter(m => m.placement?.[placement] === true);
    
    log.info(`[ToolRegistry] getToolsForPlacement('${placement}'): found ${tools.length} tools`);
    return tools;
  }

  getToolIdsForPlacement(placement: keyof ToolManifest['placement']): string[] {
    return this.getToolsForPlacement(placement).map(m => m.id);
  }

  getToolTimeout(toolId: string): number {
    const manifest = this.getManifest(toolId);
    return manifest?.performance?.expectedDuration || 600000;
  }

  async executeToolHandler(
    toolId: string,
    params: any,
    context: {
      userId: string;
      spaceId: string;
      threadId: string;
      executionId: string;
      traceId?: string;
      onProgress?: (progress: ProgressUpdate) => void;
    }
  ): Promise<ToolResult> {
    const tool = this.tools.get(toolId);
    
    if (!tool) {
      throw new Error(`Tool not found: ${toolId}`);
    }

    // Get user context
    const userStore = useUserStore.getState();
    const userPlan = userStore.plan?.plan?.toLowerCase() as 'free' | 'plus' | 'ultra' | 'pro' || 'free';
    const userEmail = userStore.profile?.skeleton?.email || userStore.profile?.skeleton?.auth0?.email || '';

    // Create riverOS context
    const riverOS = createRiverOS({
      ...context,
      userPlan,
      userEmail,
      toolId
    });

    // Execute handler
    return await tool.handler(params, riverOS);
  }

  checkPlanRequirement(toolId: string, userPlan: 'free' | 'plus' | 'ultra' | 'pro'): boolean {
    const manifest = this.manifests.get(toolId);
    
    if (!manifest || !manifest.planRequirement) {
      return true; // No requirement = available to all
    }

    const planHierarchy: Record<string, number> = { free: 0, plus: 1, ultra: 2, pro: 3 };
    const requiredLevel = planHierarchy[manifest.planRequirement as string];
    const userLevel = planHierarchy[userPlan];

    return userLevel >= requiredLevel;
  }
}

export const toolRegistry = new ToolRegistry();

